import { useMemo, useState } from 'react';

import {
   HistoricalMedicalDeviceOrder,
   HistoricalMedicalDeviceOrderQuery,
   MedicalDeviceOrderStatus,
} from '@dreryk/egabinet-ezwm-frontend-api';

import { useLazyLoadedDataConfigurationByCqrsQuery } from '@dreryk/drerykweb-main-frontend';
import { dateTimeMinuteFormatter, Ordering } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   EmptyStatePlaceholder,
   StyledFlex,
   Table,
   TableColumnConfiguration,
   TextPlaceholder,
   ValueRendererProps,
} from '@dreryk/react-components';
import { Slot, TextPattern } from '@dreryk/watermelon';

import {
   MedicalDeviceOrderEzwmAuthenticationFault,
   MedicalDeviceOrderEzwmLoginErrorsModal,
} from '../../medicalDeviceOrder/MedicalDeviceOrderEzwmLoginErrorsModal';
import { medicalDevicesTranslationKeys } from '../../translations';
import { MedicalDeviceOrdersHistorySearch } from '../MedicalDeviceOrdersHistorySearch';

import { DeviceCell } from './DeviceCell';
import { OptionsCell } from './OptionsCell';
import { MedicalDeviceApplicableStatuses, StatusCell } from './StatusCell';

const columnWidths = createDynamicColumnsWidth({
   orderDate: '153px',
   documentNumber: '161px',
   device: '100%',
   pin: '55px',
   status: '155px',
   actions: '33px',
});
const NOT_APPLICABLE_STATUSES: Array<MedicalDeviceOrderStatus> = ['NOT_SENT', 'INCORRECT'];

// eslint-disable-next-line max-lines-per-function
const useMedicalDeviceOrdersHistoryTableColumns = (
   currentVisitId: number,
   onVerificationRetryFetchSuccess: VoidFunction,
   onFault: (fault: MedicalDeviceOrderEzwmAuthenticationFault) => void
): TableColumnConfiguration<HistoricalMedicalDeviceOrder>[] => {
   return [
      {
         name: 'orderDate',
         accessor: (bean) => bean.orderDate,
         width: columnWidths.orderDate,
         formatter: (value: string) => dateTimeMinuteFormatter(value),
         label: medicalDevicesTranslationKeys.historicalMedicalDeviceOrder.creationDate,
      },
      {
         name: 'documentNumber',
         accessor: 'documentNumber',
         width: columnWidths.documentNumber,
         sortingDisabled: true,
         label: medicalDevicesTranslationKeys.historicalMedicalDeviceOrder.documentNumber,

         renderer: (rendererProps: ValueRendererProps<HistoricalMedicalDeviceOrder>) => {
            return <>{rendererProps.bean.documentNumber}</>;
         },
         placeholder: TextPlaceholder.NOT_APPLICABLE,
      },
      {
         name: 'device',
         accessor: 'documentNumber',
         width: columnWidths.device,
         sortingDisabled: true,
         label: medicalDevicesTranslationKeys.historicalMedicalDeviceOrder.device,

         renderer: (rendererProps: ValueRendererProps<HistoricalMedicalDeviceOrder>) => {
            return <DeviceCell {...rendererProps.bean} />;
         },
      },
      {
         name: 'pin',
         accessor: 'pin',
         width: columnWidths.pin,
         sortingDisabled: true,
         label: medicalDevicesTranslationKeys.historicalMedicalDeviceOrder.code,
         placeholder: TextPlaceholder.NOT_APPLICABLE,
      },
      {
         name: 'status',
         accessor: 'medicalDeviceOrderStatus',
         width: columnWidths.status,
         sortingDisabled: true,
         label: medicalDevicesTranslationKeys.historicalMedicalDeviceOrder.status,

         renderer: ({ bean }: ValueRendererProps<HistoricalMedicalDeviceOrder>) => {
            if (NOT_APPLICABLE_STATUSES.includes(bean.medicalDeviceOrderStatus)) {
               return <></>;
            }
            return (
               <StatusCell
                  id={bean.id}
                  medicalDeviceOrderStatus={bean.medicalDeviceOrderStatus as MedicalDeviceApplicableStatuses}
               />
            );
         },
      },
      {
         name: 'actions',
         accessor: () => '',

         renderer: ({ bean }: ValueRendererProps<HistoricalMedicalDeviceOrder>) => {
            return (
               <OptionsCell
                  externalId={bean.externalId}
                  medicalDeviceOrderId={bean.id}
                  visitId={currentVisitId}
                  medicalDeviceOrderStatus={bean.medicalDeviceOrderStatus}
                  onVerificationRetryFetchSuccess={onVerificationRetryFetchSuccess}
                  onFault={onFault}
                  sameAsCurrentVisit={bean.visitId === currentVisitId}
               />
            );
         },
         width: columnWidths.actions,
         sortingDisabled: true,
      },
   ];
};

type MedicalDeviceOrdersHistoryTableProps = {
   readonly patientId: number;
   readonly visitId: number;
};

const PAGE_SIZE = 10;
// eslint-disable-next-line max-lines-per-function
export const MedicalDeviceOrdersHistoryTable = (props: MedicalDeviceOrdersHistoryTableProps): JSX.Element => {
   const [ezwmAuthenticationFault, setEzwmAuthenticationFault] = useState<
      MedicalDeviceOrderEzwmAuthenticationFault | undefined
   >();
   const [searchText, setSearchText] = useState<string | null>(null);

   const orderings: Ordering<HistoricalMedicalDeviceOrder>[] = useMemo(
      () => [
         {
            property: 'orderDate',
            descending: true,
         },
      ],
      []
   );

   const lazyLoadedDataConfigurationQuery = useLazyLoadedDataConfigurationByCqrsQuery(() => {
      const query = new HistoricalMedicalDeviceOrderQuery();
      query.searchEntry = searchText ? TextPattern.anywhereIgnoreCase(searchText) : undefined;
      query.patientId = Slot.of(props.patientId);
      return query;
   }, orderings);
   const columnsConfiguration = useMedicalDeviceOrdersHistoryTableColumns(
      props.visitId,
      lazyLoadedDataConfigurationQuery.refetch,
      setEzwmAuthenticationFault
   );

   const noSearchText = searchText === null;

   const shouldHideSearchBar =
      noSearchText &&
      !lazyLoadedDataConfigurationQuery.loading &&
      lazyLoadedDataConfigurationQuery.dataPart?.data?.length === 0;

   return (
      <>
         <StyledFlex direction={'column'} gap={'20px'}>
            {shouldHideSearchBar ? null : <MedicalDeviceOrdersHistorySearch onSearchTextChanged={setSearchText} />}
            <Table<HistoricalMedicalDeviceOrder>
               testId={'ordersHistory'}
               keyExtractor={'id'}
               columns={columnsConfiguration}
               dataConfiguration={lazyLoadedDataConfigurationQuery}
               pageSize={PAGE_SIZE}
               placeholder={
                  <EmptyStatePlaceholder
                     testId={'noDeviceOrders'}
                     heading={
                        noSearchText
                           ? medicalDevicesTranslationKeys.historicalMedicalDeviceOrder.noOrdersIssued
                           : medicalDevicesTranslationKeys.historicalMedicalDeviceOrder.noMatchingOrders
                     }
                     subheading={
                        noSearchText
                           ? medicalDevicesTranslationKeys.historicalMedicalDeviceOrder.goToNewOrder
                           : undefined
                     }
                  />
               }
            />
         </StyledFlex>
         {ezwmAuthenticationFault ? (
            <MedicalDeviceOrderEzwmLoginErrorsModal
               onClose={() => setEzwmAuthenticationFault(undefined)}
               fault={ezwmAuthenticationFault}
               printableWithoutVerification={false}
            />
         ) : null}
      </>
   );
};
